// ============================================================================
// OUVRIR LE BACK-OFFICE D'UN MAGASIN CLIENT depuis la Console (doctrine d'identité §4ter) — la moitié
// navigateur. Le serveur émet un ticket d'usage unique valable deux minutes ; ce module l'apporte au
// back-office et rien d'autre.
//
// ⚠️ LE TICKET PART EN CORPS DE FORMULAIRE, JAMAIS EN URL. Un jeton dans une adresse finit dans le
// journal du serveur (Fastify journalise `req.url`), dans celui du balanceur, dans l'historique du
// navigateur et dans le `Referer` de la page suivante. Un POST ne laisse aucune de ces traces.
//
// ⚠️ L'ONGLET EST OUVERT AVANT L'APPEL RÉSEAU, dans le geste de l'utilisateur : après un `await`, le
// navigateur ne considère plus le clic comme actif et bloque l'ouverture. Et si elle est tout de même
// bloquée, on le DIT — un bouton qui ne fait rien sans expliquer pourquoi est un bouton qui ment.
// ============================================================================
import { api } from './api';

/** Poste un formulaire invisible vers une autre origine, dans l'onglet nommé. */
function postForm(action: string, fields: Record<string, string>, target: string): void {
  const f = document.createElement('form');
  f.method = 'POST';
  f.action = action;
  f.target = target;
  for (const [nom, valeur] of Object.entries(fields)) {
    const i = document.createElement('input');
    i.type = 'hidden';
    i.name = nom;
    i.value = valeur;
    f.appendChild(i);
  }
  // ⚠️ Retiré du document immédiatement après l'envoi : le ticket n'a pas à traîner dans le DOM de la
  // Console une fois qu'il est parti (il ne sert de toute façon qu'une fois).
  document.body.appendChild(f);
  f.submit();
  f.remove();
}

/**
 * Ouvre le back-office du magasin dans un nouvel onglet, avec le compte Orgazone de l'agent.
 * `reason` est le motif de l'ouverture (obligatoire, journalisé) ; `notice` reçoit le motif RÉEL de tout échec —
 * refus de droits, magasin non déclaré, BO injoignable.
 * ⚠️ À APPELER DANS LE GESTE DE L'UTILISATEUR (le clic qui valide le motif) : l'onglet s'ouvre avant tout `await`.
 */
export async function openStoreBackoffice(
  storeId: string, storeName: string, reason: string, notice: (message: string) => void,
): Promise<void> {
  const nomOnglet = `oz-bo-${storeId}`;
  const onglet = window.open('', nomOnglet);
  if (!onglet) {
    notice("Le navigateur a bloqué l'ouverture d'un onglet. Autorisez les fenêtres surgissantes pour la Console, puis réessayez.");
    return;
  }
  // L'onglet ne reste pas blanc pendant l'aller-retour : il dit ce qu'il attend.
  onglet.document.write(
    `<!doctype html><meta charset="utf-8"><title>Ouverture du back-office…</title>`
    + `<div style="font:15px/1.6 system-ui,sans-serif;max-width:34rem;margin:16vh auto;padding:0 1.5rem;color:#555">`
    + `Ouverture du back-office de « ${storeName.replace(/[<&]/g, '')} »…</div>`,
  );

  try {
    const r = await api.openStoreBackoffice(storeId, reason);
    postForm(r.entryUrl, { ticket: r.ticket }, nomOnglet);
  } catch (e) {
    // ⚠️ On referme l'onglet : le laisser sur « Ouverture… » indéfiniment ferait croire à une attente
    // alors que rien n'est en cours (§23.0). Le motif s'affiche là où l'agent a cliqué.
    onglet.close();
    notice(`Ouverture du back-office impossible : ${e instanceof Error ? e.message : String(e)}`);
  }
}
